import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { getRouteApi } from '@tanstack/react-router';
import { ChevronUp } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { Card } from '@/components/ui/card';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import type { Projects } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';
import { getFinalAreaRoom } from '@/utils/getFinalAreaRoom';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';
import type { SearchParams } from '@/utils/parseSearchParamsToSearchQuery';

import type { Estates } from '../api/estates';
import type { EstateInfoProps } from './estate-info';
import { EstatesInfiniteScroll } from './estates-infinite-scroll';
import { EstatesListCard } from './estates-list-card';

export type EstateListData = Pick<
  Estates[number],
  'id' | 'name' | 'status' | 'thumbnail' | 'createdAt'
> &
  Partial<Pick<Estates[number], 'creator' | 'estateDetails' | 'estateAreasRooms'>> & {
    availability?: Estates[number]['availability'];
    address?: {
      address?: string | null;
      postalCode?: string | null;
      city?: string | null;
    } | null;
    prices?: Projects[number]['prices'] | Estates[number]['prices'] | null;
    areaRooms?: Projects[number]['areaRooms'] | null;
    projects?: {
      id: string;
      name: string;
    } | null;
  };

interface EstatesListProps {
  data: EstateListData[];
  filters?: SearchParams;
  filterOwn?: boolean;
  infiniteScroll?: boolean;
  isPending?: boolean;
  type?: 'estate' | 'project' | 'combined';
  btnActionRender?: (props: EstateInfoProps) => JSX.Element | null;
  route: ReturnType<typeof getRouteApi>;
}

type SortField = 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';

type SortState = {
  by?: SortField;
  order?: 'asc' | 'desc';
};

type EstatesListScrollState = {
  scrollTop: number;
  estateId?: string;
  pathname?: string;
  search?: string;
};

type SortableHeaderProps = {
  field: SortField;
  currentSort?: SortState;
  children: React.ReactNode;
  className?: string;
  onSort: (field: SortField) => void;
};

const SortableHeader = ({
  field,
  currentSort,
  children,
  className = '',
  onSort,
}: SortableHeaderProps) => {
  const isActive = currentSort?.by === field;
  const isDesc = isActive && currentSort?.order === 'desc';

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={cn(
        'flex items-center gap-1 text-md font-normal leading-[28px] text-primary-dark transition-colors hover:text-primary',
        className
      )}
    >
      <span>{children}</span>
      {isActive ? (
        <div className="flex flex-col">
          <ChevronUp size={12} className={cn('transition-colors', isDesc ? 'rotate-180' : '')} />
        </div>
      ) : null}
    </button>
  );
};

export const EstatesList = ({
  data,
  filters,
  filterOwn,
  infiniteScroll = true,
  isPending = false,
  type = 'estate',
  btnActionRender,
  route,
}: EstatesListProps) => {
  const { t } = useTranslation();

  const { sort } = route.useSearch();
  const currentSort = sort as SortState | undefined;

  const navigate = route.useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const scrollKey = `scroll:${window.location.pathname}${window.location.search}`;
    const raw = window.sessionStorage.getItem(scrollKey);

    if (!raw) return;

    let parsed: EstatesListScrollState | null = null;
    let target = 0;

    try {
      parsed = JSON.parse(raw) as EstatesListScrollState;
    } catch {
      const numeric = Number(raw);
      if (!Number.isFinite(numeric) || numeric < 0) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }
      target = numeric;
    }

    const estateId = parsed?.estateId;
    const storedScrollTop = parsed?.scrollTop;

    if (parsed) {
      if (parsed.pathname && parsed.pathname !== window.location.pathname) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }
      if (parsed.search && parsed.search !== window.location.search) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }

      if (!Number.isFinite(storedScrollTop) || (storedScrollTop ?? 0) < 0) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }

      target = storedScrollTop ?? 0;
    }

    if (!Number.isFinite(target) || target < 0) {
      window.sessionStorage.removeItem(scrollKey);
      return;
    }

    const mainElement = document.querySelector<HTMLElement>('main.flex-1');
    if (!mainElement) {
      window.sessionStorage.removeItem(scrollKey);
      return;
    }

    let frameId: number | null = null;
    let attempts = 0;
    const maxAttempts = 180; // ~3s at 60fps

    const step = () => {
      attempts += 1;

      if (estateId) {
        const targetEl = document.querySelector<HTMLElement>(`[data-estate-id="${estateId}"]`);

        if (targetEl) {
          targetEl.scrollIntoView({ block: 'nearest' });
          window.sessionStorage.removeItem(scrollKey);
          return;
        }
      }

      const maxScrollTop = Math.max(mainElement.scrollHeight - mainElement.clientHeight, 0);

      if (maxScrollTop > 0) {
        const finalTop = Math.min(target, maxScrollTop);
        mainElement.scrollTop = finalTop;
      }

      if (attempts < maxAttempts) {
        frameId = window.requestAnimationFrame(step);
      } else {
        window.sessionStorage.removeItem(scrollKey);
      }
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      navigate({
        search: (prev) => {
          const prevSort = prev.sort as SortState | undefined;

          let nextSort: { by: SortField; order: 'asc' | 'desc' } | undefined;

          if (prevSort?.by === field) {
            if (prevSort.order === 'asc') {
              nextSort = { by: field, order: 'desc' };
            } else if (prevSort.order === 'desc') {
              nextSort = undefined;
            } else {
              nextSort = { by: field, order: 'asc' };
            }
          } else {
            nextSort = { by: field, order: 'asc' };
          }

          if (!nextSort) {
            const { sort: _, ...rest } = prev;
            return rest;
          }

          return {
            ...prev,
            sort: nextSort,
          };
        },
      });
    },
    [navigate]
  );

  const renderCard = (estate: EstateListData | Estates[number] | Projects[number]) => (
    <ListCard estate={estate as EstateListData} btnActionRender={btnActionRender} />
  );

  return (
    <Card className="border border-slate-200 bg-white px-3 py-3.5 shadow-none sm:px-6">
      <div className="overflow-x-auto">
        <div className="min-w-[1040px]">
          <div className="mb-3 flex items-center px-2 sm:px-4">
            <SortableHeader
              field="name"
              className="ml-4 min-w-[520px] flex-[2.5]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('title')}
            </SortableHeader>
            <SortableHeader
              field="price"
              className="w-[120px]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('price')}
            </SortableHeader>
            <SortableHeader
              field="area"
              className="w-[100px]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('area')}
            </SortableHeader>
            <SortableHeader
              field="rooms"
              className="w-[60px]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('rooms')}
            </SortableHeader>
            <SortableHeader
              field="creator"
              className="w-[120px]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('author')}
            </SortableHeader>
            <SortableHeader
              field="createdAt"
              className="w-[90px]"
              currentSort={currentSort}
              onSort={handleSort}
            >
              {t('created')}
            </SortableHeader>
            {btnActionRender ? (
              <div className="w-[80px] flex-1 text-md font-normal text-primary-dark" />
            ) : null}
          </div>

          <div className="flex flex-col gap-y-[13px]">
            {!isPending && data.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                {t('notMatchingResults')}
              </div>
            ) : null}
            {data.map((estate) => (
              <ListCard key={estate.id} estate={estate} btnActionRender={btnActionRender} />
            ))}
            {infiniteScroll ? (
              type === 'combined' ? (
                <>
                  <EstatesInfiniteScroll
                    filters={filters}
                    filterOwn={filterOwn}
                    sort={sort}
                    type="estate"
                    cardRenderer={renderCard}
                  />
                  <EstatesInfiniteScroll
                    filters={filters}
                    filterOwn={filterOwn}
                    sort={sort}
                    type="project"
                    cardRenderer={renderCard}
                  />
                </>
              ) : (
                <EstatesInfiniteScroll
                  filters={filters}
                  filterOwn={filterOwn}
                  sort={sort}
                  type={type}
                  cardRenderer={renderCard}
                />
              )
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
};

const ListCard = ({
  estate,
  btnActionRender,
}: Pick<EstatesListProps, 'btnActionRender'> & {
  estate: EstateListData;
}) => {
  const { data: estateOptions } = useEstateOptions();

  const price = getFinalPrice(estate);
  const roomsCount = getFinalAreaRoom(estate);
  const livingArea = getFinalLivingArea(estate);
  const creatorName = [estate.creator?.firstName, estate.creator?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <EstatesListCard
      propertyLevel={'estateDetails' in estate ? PROPERTY_LEVELS.OBJECT : PROPERTY_LEVELS.PROJECT}
      status={estate.status}
      availability={estate.availability}
      id={estate.id}
      address={estate.address}
      marketType={estate.estateDetails?.marketType}
      propertyType={estate.estateDetails?.propertyType}
      usageType={estate.estateDetails?.usageType}
      livingArea={livingArea}
      externalId={estate.estateDetails?.externalId}
      name={estate.name}
      price={price}
      roomsCount={roomsCount}
      createdAt={estate.createdAt}
      creator={creatorName}
      thumbnailId={estate.thumbnail?.mediaId || ''}
      btnActionRender={btnActionRender}
      estateOptions={estateOptions}
      projects={estate?.projects}
    />
  );
};
